import type { SensorAutoSaveStatus } from "../sensor/sensor-auto-save";

export interface AutoSaveDisplay {
	areChangesSaved: boolean;
	isSaving: boolean;
	isVisible: boolean;
	label?: string;
}

interface AutoSaveInputs {
	autoSaveStatus: SensorAutoSaveStatus;
	canUndoEdit: boolean;
	isEditDirty: boolean;
	isUpdatePending: boolean;
}

/** True while the draft differs from the original, undo history exists, or a save is in flight. */
export const hasAutoSaveEdits = ({
	autoSaveStatus,
	canUndoEdit,
	isEditDirty,
	isUpdatePending,
}: AutoSaveInputs) =>
	isEditDirty ||
	canUndoEdit ||
	isUpdatePending ||
	autoSaveStatus === "pending" ||
	autoSaveStatus === "saving";

export const getAutoSaveDisplay = ({
	autoSaveStatus,
	canUndoEdit,
	isEditDirty,
	isUpdatePending,
}: AutoSaveInputs): AutoSaveDisplay => {
	const hasEdited = hasAutoSaveEdits({
		autoSaveStatus,
		canUndoEdit,
		isEditDirty,
		isUpdatePending,
	});
	const showDone = autoSaveStatus === "idle" || autoSaveStatus === "saved";
	const isSaving = hasEdited && (isUpdatePending || !showDone);
	const label = !hasEdited
		? undefined
		: isSaving
			? "Saving..."
			: autoSaveStatus === "saved"
				? "Saved"
				: undefined;
	const areChangesSaved = canUndoEdit && autoSaveStatus === "saved";

	return {
		areChangesSaved,
		isSaving,
		isVisible: hasEdited && Boolean(label || areChangesSaved),
		label,
	};
};

/** @deprecated Use hasAutoSaveEdits */
export const hasSensorEdits = ({
	autoSaveStatus,
	canUndoSensorEdit,
	isEditDirty,
	isUpdatedSensorPending,
}: {
	autoSaveStatus: SensorAutoSaveStatus;
	canUndoSensorEdit: boolean;
	isEditDirty: boolean;
	isUpdatedSensorPending: boolean;
}) =>
	hasAutoSaveEdits({
		autoSaveStatus,
		canUndoEdit: canUndoSensorEdit,
		isEditDirty,
		isUpdatePending: isUpdatedSensorPending,
	});
